import {
  CertificateRecord,
  LogAnomaly,
  LogReplay,
  PublicEvent,
  PublicIntendedUse,
  PublicWrp,
  REGISTRATION_FIELDS,
  REGISTRATION_RECORD_TYPE,
  RegistrationContent,
  RegistrationRecordEntry,
  compareSequence,
  currentRegistration,
  wrpStatus,
} from "./transparency-log";
import { IntendedUse, deriveIntendedUses } from "./requested-data";
import { TreeHeadCheck } from "./merkle";
import { SignatureCheck } from "./signature-verification";

export type Severity = "high" | "warning" | "info";

export interface WatchFinding {
  id: string;
  rule: string;
  title: string;
  severity: Severity;
  /** Empty for findings about the log as a whole. */
  wrpId: string;
  observedAt: string;
  detail: string;
  evidence: PublicEvent[];
  /** The API response the finding comes from, when it is not a log event. */
  source?: string;
}

export interface RuleDescription {
  rule: string;
  title: string;
  severity: Severity;
  description: string;
}

/** The rule catalogue, shown in the UI so every finding is explainable. */
export const RULES: RuleDescription[] = [
  {
    rule: "R-01",
    title: "Log integrity",
    severity: "high",
    description:
      "Duplicate events, non-increasing or skipped sequence numbers, an event of unknown type, issuance without certificate, a registration_recorded event without record, a certificate issued or a record published twice, or a revocation that does not match an earlier issuance of the same WRP.",
  },
  {
    rule: "R-02",
    title: "Certificate signature or content",
    severity: "high",
    description:
      "The certificate is not signed by the registrar's trust anchor or by a sub CA it certified (invalid signature, other signer or unsupported algorithm), cannot be parsed, or names another subject identifier than the event wrp_id.",
  },
  {
    rule: "R-03",
    title: "Issuance burst",
    severity: "warning",
    description:
      "More than 10 access certificates for one WRP within any 10-minute window.",
  },
  {
    rule: "R-04",
    title: "Short-lived certificate",
    severity: "warning",
    description:
      "A certificate revoked less than 15 minutes after issuance. Replacements (reason superseded) are expected and excluded.",
  },
  {
    rule: "R-05",
    title: "Inconsistent registration data",
    severity: "warning",
    description:
      "Valid registration certificates of one WRP disagree on WRP-level data. Wallets may show outdated contact or policy information.",
  },
  {
    rule: "R-06",
    title: "Register disagrees with log",
    severity: "high",
    description:
      "The register API at the same as_of_sequence (derivation rules version 3) reports different WRPs, certificates, revocations, registration records (in log order) or registration status than the replayed log, or /intended-uses groups the registration certificates into other intended uses than the identifiers derived from them. The status comes from the current registration record; without one, only suspended and cancelled are compared, as active versus historic also depends on certificate expiry.",
  },
  {
    rule: "R-07",
    title: "Superseded without successor",
    severity: "warning",
    description:
      "A certificate revoked as superseded without a new certificate of the same type for the same WRP issued in between. A replacement should be published together with the revocation.",
  },
  {
    rule: "R-08",
    title: "Attestation type not well-formed",
    severity: "info",
    description:
      'A current intended use requests a credential without vct or doctype, or with one that contains whitespace, quotation marks or commas, or that is neither a URI or URN (contains ":") nor a reverse domain name (contains "."). A wallet cannot match such a request to a credential.',
  },
  {
    rule: "R-09",
    title: "mdoc claim path not namespace and element",
    severity: "info",
    description:
      "A claim requested from an mso_mdoc credential does not have exactly two string segments, the namespace and the data element identifier (OpenID4VP, DCQL claims path for mdoc).",
  },
  {
    rule: "R-10",
    title: "No claims declared",
    severity: "info",
    description:
      "A current intended use requests a credential without listing claims, so the registration does not show which attributes the WRP asks for.",
  },
  {
    rule: "R-11",
    title: "Registration payload outside TS 119 475",
    severity: "info",
    description:
      "An unrevoked registration certificate lacks a required field (name, sub, country, registry_uri, entitlements, iat), contains fields TS 119 475 v1.2.1 does not define, or has purpose or service descriptions that are not { lang, value } objects.",
  },
  {
    rule: "R-12",
    title: "Registration record not valid",
    severity: "high",
    description:
      "A registration record is not signed by a statement signer of the registrar (ES256, x5c[0] issued by the trust anchor or by a sub CA it certified, with extended key usage id-kp-documentSigning), cannot be decoded, has another typ than wrp-registration+jwt, states no registration status (active, suspended, cancelled), or names another WRP (sub) than the event wrp_id.",
  },
  {
    rule: "R-13",
    title: "Tree head disagrees with the log",
    severity: "high",
    description:
      "The tree head the registrar serves for the replayed log size has another root hash than the RFC 6962 Merkle tree over the replayed events, or the consistency proof from the tree head verified earlier in this session does not verify. The log shown to this monitor is then not the log the registrar commits to, or its history was rewritten.",
  },
];

const BURST_WINDOW_MS = 10 * 60 * 1000;
const BURST_THRESHOLD = 10;
const SHORT_LIVED_MS = 15 * 60 * 1000;

const ANOMALY_TEXT: Record<LogAnomaly["kind"], [string, string]> = {
  duplicate_event: [
    "Duplicate event identifier",
    "The event identifier appears more than once in the log.",
  ],
  sequence_not_increasing: [
    "Event sequence not increasing",
    "The sequence number is not greater than the previous event.",
  ],
  sequence_gap: [
    "Sequence numbers skipped",
    "The log skips sequence numbers. The registrar's log is gap-free, so a missing number means an event was withheld from this feed or the log was altered.",
  ],
  unknown_event_type: [
    "Event of unknown type",
    "The monitor does not know the event type, so it cannot replay the event. The register may depend on it.",
  ],
  missing_artifact: [
    "Issued event has no certificate",
    "The issuance event does not contain the published certificate.",
  ],
  missing_record: [
    "Recorded event has no registration record",
    "The registration_recorded event does not contain the published record.",
  ],
  duplicate_issuance: [
    "Certificate issued twice",
    "A certificate with the same fingerprint was already issued earlier in the log.",
  ],
  duplicate_record: [
    "Registration record published twice",
    "A record with the same fingerprint was already published earlier in the log. A repeated record can restore an earlier registration status.",
  ],
  unknown_revocation: [
    "Revocation of unknown certificate",
    "The revoked fingerprint does not match any certificate issued earlier in the log.",
  ],
  duplicate_revocation: [
    "Certificate revoked twice",
    "The certificate was already revoked earlier in the log.",
  ],
  revocation_wrp_mismatch: [
    "Revocation for a different WRP",
    "The revocation names another WRP than the issuance of the same certificate.",
  ],
};

/**
 * Evaluates every rule. The register is compared only for the endpoints
 * that could be read. When it was read at an earlier sequence than the end
 * of the replayed log, `register.replayed` is the replay up to that sequence
 * and R-06 compares with it; every other rule uses the full replay. The tree
 * head check (R-13) is done while synchronising, as it needs the network.
 */
export function evaluateFindings(
  replayed: LogReplay,
  intendedUses: IntendedUse[],
  register: {
    wrps?: PublicWrp[];
    intendedUses?: PublicIntendedUse[];
    replayed?: LogReplay;
  } = {},
  treeHead?: TreeHeadCheck,
): WatchFinding[] {
  const certificates = [...replayed.certificates.values()];
  const records = [...replayed.registrations.values()].flat();
  const current = intendedUses.filter((use) => use.current);
  const compared = register.replayed
    ? {
        certificates: [...register.replayed.certificates.values()],
        registrations: register.replayed.registrations,
        intendedUses: deriveIntendedUses(register.replayed),
      }
    : { certificates, registrations: replayed.registrations, intendedUses };
  const findings = [
    ...replayed.anomalies.map(integrityFinding),
    ...certificates.flatMap(contentFindings),
    ...records.flatMap(recordFindings),
    ...(treeHead ? treeHeadFindings(treeHead) : []),
    ...burstFindings(certificates),
    ...shortLivedFindings(certificates),
    ...inconsistentRegistrationFindings(certificates),
    ...supersededWithoutSuccessorFindings(certificates),
    ...(register.wrps
      ? registerFindings(
          compared.certificates,
          compared.registrations,
          register.wrps,
        )
      : []),
    ...(register.intendedUses
      ? intendedUseFindings(compared.intendedUses, register.intendedUses)
      : []),
    ...attestationTypeFindings(current),
    ...mdocClaimPathFindings(current),
    ...undeclaredClaimFindings(current),
    ...payloadSchemaFindings(certificates),
  ];

  return findings.sort(
    (left, right) =>
      severityRank(right.severity) - severityRank(left.severity) ||
      Date.parse(right.observedAt) - Date.parse(left.observedAt),
  );
}

function integrityFinding(anomaly: LogAnomaly): WatchFinding {
  const [title, detail] = ANOMALY_TEXT[anomaly.kind];
  return finding(
    "R-01",
    anomaly.kind,
    title,
    "high",
    anomaly.event,
    detail,
    anomaly.related ? [anomaly.related, anomaly.event] : [anomaly.event],
  );
}

const SIGNATURE_TEXT: Partial<
  Record<CertificateRecord["signature"], [string, string]>
> = {
  invalid: [
    "Certificate signature invalid",
    "The signature does not verify with the registrar's trust anchor or the sub CA it certified.",
  ],
  unknown_signer: [
    "Certificate signed by an unknown signer",
    "The certificate names another signer than the registrar's trust anchor.",
  ],
  unsupported: [
    "Certificate signature algorithm not accepted",
    "The certificate uses a signature algorithm the monitor does not accept, so it cannot be attributed to the registrar.",
  ],
};

function contentFindings(record: CertificateRecord): WatchFinding[] {
  // Any result other than valid is high: an attacker must not be able to
  // downgrade a finding by changing the algorithm identifier.
  const signature = SIGNATURE_TEXT[record.signature];
  if (signature) {
    return [
      finding(
        "R-02",
        `signature-${record.signature}`,
        signature[0],
        "high",
        record.issued,
        signature[1],
        [record.issued],
      ),
    ];
  }
  if (record.parseError) {
    return [
      finding(
        "R-02",
        "unreadable",
        "Unreadable certificate",
        "high",
        record.issued,
        `The published certificate could not be parsed: ${record.parseError}.`,
        [record.issued],
      ),
    ];
  }
  const subject =
    record.registration?.sub ?? record.access?.organizationIdentifier;
  if (subject && subject !== record.wrpId) {
    return [
      finding(
        "R-02",
        "subject",
        "Certificate subject differs from event",
        "high",
        record.issued,
        `The certificate names ${subject}, the event names ${record.wrpId}.`,
        [record.issued],
      ),
    ];
  }
  return [];
}

const RECORD_SIGNATURE_TEXT: Partial<Record<SignatureCheck, [string, string]>> =
  {
    invalid: [
      "Registration record signature invalid",
      "The signature does not verify with the key of the signer certificate (x5c[0]).",
    ],
    unknown_signer: [
      "Registration record signed by an unknown signer",
      "The signer certificate (x5c[0]) is missing or not issued by the registrar's trust anchor, directly or through a sub CA it certified (x5c[1] or caIssuers).",
    ],
    wrong_key_usage: [
      "Registration record not signed by a statement signer",
      "The signer certificate (x5c[0]) chains to the registrar's trust anchor, but is not certified for document signing (extended key usage id-kp-documentSigning), so it is no statement signer.",
    ],
    unsupported: [
      "Registration record signature algorithm not accepted",
      "The record or its signer certificate uses a signature algorithm the monitor does not accept, so it cannot be attributed to the registrar.",
    ],
  };

/**
 * Like certificates, any result other than a valid record is high. A record
 * that is not even a compact JWS is reported as such, not as a bad signature.
 */
function recordFindings(entry: RegistrationRecordEntry): WatchFinding[] {
  const report = (kind: string, title: string, detail: string) => [
    finding("R-12", kind, title, "high", entry.event, detail, [entry.event]),
  ];
  if (entry.parseError) {
    return report(
      "unreadable",
      "Unreadable registration record",
      `The published record could not be decoded: ${entry.parseError}. The register ignores it.`,
    );
  }
  const signature = RECORD_SIGNATURE_TEXT[entry.signature];
  if (signature) {
    return report(`signature-${entry.signature}`, ...signature);
  }
  if (entry.type !== REGISTRATION_RECORD_TYPE) {
    return report(
      "type",
      "Registration record of another type",
      `The protected header has ${entry.type ? `typ ${entry.type}` : "no typ"}, not ${REGISTRATION_RECORD_TYPE}, so the signed statement is not a registration record.`,
    );
  }
  if (!entry.status) {
    return report(
      "status",
      "Registration record without status",
      "The record states no registration_status (active, suspended or cancelled). The register then takes the status from the certificates.",
    );
  }
  const subject = entry.payload?.sub;
  if (subject !== entry.wrpId) {
    return report(
      "subject",
      "Registration record names another WRP",
      `The record names ${typeof subject === "string" ? subject : "no WRP"} as sub, the event names ${entry.wrpId}.`,
    );
  }
  return [];
}

/** R-13: the tree head is about the whole log, so it names no WRP. */
function treeHeadFindings(check: TreeHeadCheck): WatchFinding[] {
  if (check.status === "unavailable") return [];
  const findings: WatchFinding[] = [];
  const logFinding = (
    kind: string,
    title: string,
    detail: string,
    source: string,
  ): WatchFinding => ({
    id: `R-13:${kind}:${check.treeSize}`,
    rule: "R-13",
    title,
    severity: "high",
    wrpId: "",
    observedAt: check.checkedAt,
    detail,
    evidence: [],
    source,
  });
  if (check.head.tree_size !== check.treeSize) {
    findings.push(
      logFinding(
        "size",
        "Tree head for another log size",
        `The tree head was requested for ${check.treeSize} events, the registrar answered for ${check.head.tree_size}.`,
        check.link,
      ),
    );
  } else if (check.head.root_hash.toLowerCase() !== check.computedRoot) {
    findings.push(
      logFinding(
        "root",
        "Tree head root differs from the log",
        `The registrar's tree head for ${check.treeSize} events has the root hash ${check.head.root_hash}; the events replayed by this monitor give ${check.computedRoot}. The registrar commits to another log than it serves.`,
        check.link,
      ),
    );
  }
  // A proof that could not be read is not evidence; it is read again.
  if (check.consistency?.valid === false) {
    findings.push(
      logFinding(
        "consistency",
        "Tree head not consistent with an earlier one",
        `The consistency proof from the tree head for ${check.consistency.fromSize} events, verified earlier in this session, to the tree head for ${check.treeSize} events does not verify. The tree for ${check.treeSize} events does not extend the earlier one, so events up to #${check.consistency.fromSize} were changed, or the proof is wrong.`,
        check.consistency.link,
      ),
    );
  }
  return findings;
}

/** Sliding 10-minute window per WRP over the whole history. */
function burstFindings(certificates: CertificateRecord[]): WatchFinding[] {
  const byWrp = groupBy(
    certificates.filter((record) => record.type === "access_certificate"),
    (record) => record.wrpId,
  );
  const findings: WatchFinding[] = [];

  for (const records of byWrp.values()) {
    const issued = records
      .map((record) => record.issued)
      .sort((left, right) => time(left) - time(right));
    let start = 0;
    let reportedUntil = -1;
    for (let end = 0; end < issued.length; end++) {
      while (time(issued[end]) - time(issued[start]) > BURST_WINDOW_MS) {
        start++;
      }
      const count = end - start + 1;
      if (count > BURST_THRESHOLD && start > reportedUntil) {
        const window = issued.slice(start, end + 1);
        findings.push(
          finding(
            "R-03",
            "burst",
            "Access certificate issuance burst",
            "warning",
            issued[end],
            `${count} access certificates were issued within ten minutes.`,
            window,
          ),
        );
        reportedUntil = end;
      }
    }
  }
  return findings;
}

function shortLivedFindings(certificates: CertificateRecord[]): WatchFinding[] {
  return certificates.flatMap((record) => {
    if (!record.revoked || record.revoked.revocation_reason === "superseded")
      return [];
    const age =
      Date.parse(record.revoked.effective_at || record.revoked.published_at) -
      time(record.issued);
    if (age < 0 || age >= SHORT_LIVED_MS) return [];
    return [
      finding(
        "R-04",
        "short-lived",
        "Short-lived certificate",
        "warning",
        record.revoked,
        `The ${label(record)} was revoked ${Math.max(1, Math.round(age / 60000))} minute(s) after issuance.`,
        [record.issued, record.revoked],
      ),
    ];
  });
}

const WRP_LEVEL_FIELDS: Array<keyof RegistrationContent> = [
  "name",
  "sub_ln",
  "info_uri",
  "support_uri",
  "entitlements",
  "srv_description",
  "intermediary",
];

/**
 * A change of WRP-level data must reissue all active registration
 * certificates. Valid certificates that still disagree show stale data.
 */
function inconsistentRegistrationFindings(
  certificates: CertificateRecord[],
): WatchFinding[] {
  const valid = certificates.filter(
    (record) => record.registration && !record.revoked,
  );
  return [...groupBy(valid, (record) => record.wrpId).values()].flatMap(
    (records) => {
      if (records.length < 2) return [];
      const differing = WRP_LEVEL_FIELDS.filter(
        (field) =>
          new Set(
            records.map((record) =>
              JSON.stringify(record.registration![field] ?? null),
            ),
          ).size > 1,
      );
      if (differing.length === 0) return [];
      const evidence = records
        .map((record) => record.issued)
        .sort((left, right) =>
          compareSequence(left.sequence_number, right.sequence_number),
        );
      return [
        finding(
          "R-05",
          "stale",
          "Valid registration certificates disagree",
          "warning",
          evidence[evidence.length - 1],
          `${records.length} valid registration certificates differ in: ${differing.join(", ")}.`,
          evidence,
        ),
      ];
    },
  );
}

/** Compares the register API with the replay at the same log position. */
/**
 * The registrar replaces a certificate by issuing the successor and revoking
 * the predecessor as superseded in one transaction.
 */
function supersededWithoutSuccessorFindings(
  certificates: CertificateRecord[],
): WatchFinding[] {
  return certificates.flatMap((record) => {
    const revoked = record.revoked;
    if (revoked?.revocation_reason !== "superseded") return [];
    const hasSuccessor = certificates.some(
      (other) =>
        other !== record &&
        other.wrpId === record.wrpId &&
        other.type === record.type &&
        compareSequence(
          other.issued.sequence_number,
          record.issued.sequence_number,
        ) > 0 &&
        compareSequence(other.issued.sequence_number, revoked.sequence_number) <
          0,
    );
    return hasSuccessor
      ? []
      : [
          finding(
            "R-07",
            "no-successor",
            "Superseded without successor",
            "warning",
            revoked,
            `The ${label(record)} was revoked as superseded, but no replacement was published before the revocation.`,
            [record.issued, revoked],
          ),
        ];
  });
}

function registerFindings(
  certificates: CertificateRecord[],
  registrations: Map<string, RegistrationRecordEntry[]>,
  register: PublicWrp[],
): WatchFinding[] {
  const local = groupBy(certificates, (record) => record.wrpId);
  // Records that cannot be decoded are not part of the register (rule 3).
  const recorded = new Map(
    [...registrations]
      .map(
        ([wrpId, entries]) =>
          [wrpId, entries.filter((entry) => entry.payload)] as const,
      )
      .filter(([, entries]) => entries.length > 0),
  );
  const remote = new Map(register.map((wrp) => [wrp.wrp_id, wrp]));
  const findings: WatchFinding[] = [];

  for (const wrpId of new Set([...local.keys(), ...recorded.keys()])) {
    const records = local.get(wrpId) ?? [];
    const entries = recorded.get(wrpId) ?? [];
    const evidence = [
      ...records.map((record) => record.issued),
      ...entries.map((entry) => entry.event),
    ].sort((left, right) =>
      compareSequence(left.sequence_number, right.sequence_number),
    );
    const latest = evidence[evidence.length - 1];
    const wrp = remote.get(wrpId);
    if (!wrp) {
      findings.push(
        finding(
          "R-06",
          "missing-wrp",
          "WRP missing from register",
          "high",
          latest,
          "The log contains certificates or registration records for this WRP, but the register does not list it.",
          evidence,
        ),
      );
      continue;
    }

    const problems = [
      ...compareCertificates(records, wrp),
      ...compareRegistrations(entries, wrp),
    ];
    // The status of a registration record is exact. Without one only the
    // Art. 9 statuses are compared: active vs historic also depends on
    // certificate expiry, which the monitor does not evaluate.
    const status = wrpStatus(records, entries);
    const exact = Boolean(currentRegistration(entries)?.status);
    const art9 = ["suspended", "cancelled"];
    if (
      wrp.status &&
      wrp.status !== status &&
      (exact || art9.includes(wrp.status) || art9.includes(status))
    ) {
      problems.push(
        `The register reports status ${wrp.status}, the log implies ${status}.`,
      );
    }
    if (problems.length > 0) {
      findings.push(
        finding(
          "R-06",
          "wrp",
          "Register entry differs from log",
          "high",
          latest,
          problems.join(" "),
          evidence,
        ),
      );
    }
  }

  for (const wrp of register) {
    if (!local.has(wrp.wrp_id) && !recorded.has(wrp.wrp_id)) {
      findings.push({
        id: `R-06:unbacked:${wrp.wrp_id}`,
        rule: "R-06",
        title: "Register lists WRP that is not in the log",
        severity: "high",
        wrpId: wrp.wrp_id,
        observedAt: new Date(0).toISOString(),
        detail:
          "The register contains a WRP for which neither a certificate nor a registration record was published in the log.",
        evidence: [],
      });
    }
  }
  return findings;
}

/** The register lists the WRP's records in log order (rule 3). */
function compareRegistrations(
  entries: RegistrationRecordEntry[],
  wrp: PublicWrp,
): string[] {
  const local = entries.map((entry) => entry.fingerprint);
  const remote = (wrp.registration_records ?? []).map(
    (record) => record.record_fingerprint,
  );
  if (
    local.length === remote.length &&
    local.every((print, index) => print === remote[index])
  ) {
    return [];
  }
  const missing = local.filter((print) => !remote.includes(print)).length;
  const extra = remote.filter((print) => !local.includes(print)).length;
  return [
    ...(missing
      ? [`${missing} registration record(s) from the log are missing.`]
      : []),
    ...(extra ? [`${extra} registration record(s) are not in the log.`] : []),
    ...(!missing && !extra
      ? ["The registration records differ in order or number from the log."]
      : []),
  ];
}

/**
 * Compares /intended-uses with the intended uses derived from the log: the
 * same identifiers, each for the same WRP and the same registration
 * certificates. Status is not compared, as it depends on certificate expiry.
 */
function intendedUseFindings(
  local: IntendedUse[],
  remote: PublicIntendedUse[],
): WatchFinding[] {
  const key = (wrpId: string, id: string) => `${wrpId}\n${id}`;
  const remoteByKey = new Map(
    remote.map((entry) => [key(entry.wrp_id, entry.intended_use_id), entry]),
  );
  const localKeys = new Set(local.map((use) => key(use.wrpId, use.id)));
  const problems = new Map<
    string,
    {
      missing: number;
      extra: number;
      differing: number;
      evidence: PublicEvent[];
    }
  >();
  const problemsOf = (wrpId: string) => {
    const entry = problems.get(wrpId) ?? {
      missing: 0,
      extra: 0,
      differing: 0,
      evidence: [],
    };
    problems.set(wrpId, entry);
    return entry;
  };

  for (const use of local) {
    const entry = remoteByKey.get(key(use.wrpId, use.id));
    const fingerprints = use.certificates.map((record) => record.fingerprint);
    if (!entry) {
      const wrp = problemsOf(use.wrpId);
      wrp.missing++;
      wrp.evidence.push(...use.certificates.map((record) => record.issued));
    } else if (!sameSet(fingerprints, entry.certificate_fingerprints ?? [])) {
      const wrp = problemsOf(use.wrpId);
      wrp.differing++;
      wrp.evidence.push(...use.certificates.map((record) => record.issued));
    }
  }
  for (const entry of remote) {
    if (!localKeys.has(key(entry.wrp_id, entry.intended_use_id))) {
      problemsOf(entry.wrp_id).extra++;
    }
  }

  return [...problems.entries()].map(([wrpId, problem]) => {
    const evidence = problem.evidence.sort((left, right) =>
      compareSequence(left.sequence_number, right.sequence_number),
    );
    const detail = [
      problem.missing &&
        `${problem.missing} intended use(s) derived from the log are missing.`,
      problem.differing &&
        `${problem.differing} intended use(s) list other registration certificates.`,
      problem.extra &&
        `${problem.extra} intended use(s) cannot be derived from the log.`,
    ]
      .filter(Boolean)
      .join(" ");
    const latest = evidence[evidence.length - 1];
    return {
      id: `R-06:intended-uses:${wrpId}`,
      rule: "R-06",
      title: "Register intended uses differ from log",
      severity: "high" as const,
      wrpId,
      observedAt: latest
        ? latest.effective_at || latest.published_at
        : new Date(0).toISOString(),
      detail,
      evidence,
    };
  });
}

function sameSet(left: string[], right: string[]): boolean {
  const set = new Set(left);
  return (
    set.size === new Set(right).size && right.every((item) => set.has(item))
  );
}

// --- Data quality of the registered content (info) -------------------------------

/** vct is a URI or URN, doctype a reverse domain name. */
function malformedType(type: string): boolean {
  return /[\s"',]/.test(type) || !/[:.]/.test(type);
}

function attestationTypeFindings(uses: IntendedUse[]): WatchFinding[] {
  return perWrp(uses, (use) =>
    use.credentials.flatMap((credential) =>
      credential.types.length
        ? credential.types.filter(malformedType).map((type) => `"${type}"`)
        : ["a credential without vct or doctype"],
    ),
  ).map(({ wrpId, values, evidence }) =>
    groupedFinding(
      "R-08",
      "Attestation type not well-formed",
      wrpId,
      evidence,
      `Requests ${list(values)}.`,
    ),
  );
}

function mdocClaimPathFindings(uses: IntendedUse[]): WatchFinding[] {
  return perWrp(uses, (use) =>
    use.credentials
      .filter((credential) => credential.format === "mso_mdoc")
      .flatMap((credential) => credential.claims ?? [])
      .filter(
        (claim) =>
          claim.path.length !== 2 ||
          claim.path.some((segment) => typeof segment !== "string"),
      )
      .map((claim) => claim.normalizedPath),
  ).map(({ wrpId, values, evidence }) =>
    groupedFinding(
      "R-09",
      "mdoc claim path not namespace and element",
      wrpId,
      evidence,
      `Claim paths without exactly a namespace and a data element: ${list(values)}.`,
    ),
  );
}

function undeclaredClaimFindings(uses: IntendedUse[]): WatchFinding[] {
  return perWrp(uses, (use) =>
    use.credentials
      .filter((credential) => !credential.claims)
      .map((credential) => credential.types.join(", ") || credential.format),
  ).map(({ wrpId, values, evidence, uses: count }) =>
    groupedFinding(
      "R-10",
      "No claims declared",
      wrpId,
      evidence,
      `${count} intended use(s) request ${list(values)} without listing claims.`,
    ),
  );
}

const REQUIRED_FIELDS: Array<
  [keyof RegistrationContent, (value: unknown) => boolean]
> = [
  ["name", (value) => typeof value === "string" && value.length > 0],
  ["sub", (value) => typeof value === "string" && value.length > 0],
  ["country", (value) => typeof value === "string" && value.length === 2],
  ["registry_uri", (value) => typeof value === "string" && value.length > 0],
  ["entitlements", (value) => Array.isArray(value) && value.length > 0],
  ["iat", (value) => Number.isInteger(value) && (value as number) > 0],
];

function payloadSchemaFindings(
  certificates: CertificateRecord[],
): WatchFinding[] {
  const byWrp = new Map<
    string,
    { problems: Set<string>; evidence: PublicEvent[] }
  >();
  for (const record of certificates) {
    const payload = record.registration;
    if (!payload || record.revoked) continue;
    const problems = [
      ...REQUIRED_FIELDS.filter(([field, valid]) => !valid(payload[field])).map(
        ([field]) => `missing or invalid ${field}`,
      ),
      ...Object.keys(payload)
        .filter((field) => !REGISTRATION_FIELDS.has(field))
        .map((field) => `undefined field ${field}`),
      ...(localizedTexts(payload.purpose)
        ? []
        : ["purpose not { lang, value }"]),
      ...(localizedTexts(
        Array.isArray(payload.srv_description)
          ? payload.srv_description.flat()
          : payload.srv_description,
      )
        ? []
        : ["srv_description not { lang, value }"]),
    ];
    if (!problems.length) continue;
    const entry = byWrp.get(record.wrpId) ?? {
      problems: new Set<string>(),
      evidence: [],
    };
    problems.forEach((problem) => entry.problems.add(problem));
    entry.evidence.push(record.issued);
    byWrp.set(record.wrpId, entry);
  }
  return [...byWrp.entries()].map(([wrpId, { problems, evidence }]) =>
    groupedFinding(
      "R-11",
      "Registration payload outside TS 119 475",
      wrpId,
      evidence,
      `${evidence.length} unrevoked registration certificate(s): ${[...problems].join("; ")}.`,
    ),
  );
}

/** Absent, or an array of { lang, value } string pairs. */
function localizedTexts(value: unknown): boolean {
  return (
    value === undefined ||
    (Array.isArray(value) &&
      value.every(
        (entry) =>
          typeof entry === "object" &&
          entry !== null &&
          typeof entry.lang === "string" &&
          typeof entry.value === "string",
      ))
  );
}

/** Collects the offending values of current intended uses per WRP. */
function perWrp(
  uses: IntendedUse[],
  offending: (use: IntendedUse) => string[],
): Array<{
  wrpId: string;
  values: string[];
  evidence: PublicEvent[];
  uses: number;
}> {
  const byWrp = new Map<
    string,
    { values: Set<string>; evidence: PublicEvent[]; uses: number }
  >();
  for (const use of uses) {
    const values = offending(use);
    if (!values.length) continue;
    const entry = byWrp.get(use.wrpId) ?? {
      values: new Set<string>(),
      evidence: [],
      uses: 0,
    };
    values.forEach((value) => entry.values.add(value));
    entry.evidence.push(
      ...use.certificates
        .filter((record) => !record.revoked)
        .map((record) => record.issued),
    );
    entry.uses++;
    byWrp.set(use.wrpId, entry);
  }
  return [...byWrp.entries()].map(([wrpId, entry]) => ({
    wrpId,
    values: [...entry.values],
    evidence: entry.evidence,
    uses: entry.uses,
  }));
}

function groupedFinding(
  rule: string,
  title: string,
  wrpId: string,
  evidence: PublicEvent[],
  detail: string,
): WatchFinding {
  const ordered = evidence.sort((left, right) =>
    compareSequence(left.sequence_number, right.sequence_number),
  );
  return {
    id: `${rule}:${wrpId}`,
    rule,
    title,
    severity: "info",
    wrpId,
    observedAt: ordered[ordered.length - 1].published_at,
    detail,
    evidence: ordered,
  };
}

/** The first values and how many more there are. */
function list(values: string[], shown = 5): string {
  const head = values.slice(0, shown).join(", ");
  return values.length > shown
    ? `${head} and ${values.length - shown} more`
    : head;
}

function compareCertificates(
  records: CertificateRecord[],
  wrp: PublicWrp,
): string[] {
  const problems: string[] = [];
  const remote = new Map(
    wrp.certificates.map((certificate) => [
      certificate.certificate_fingerprint,
      certificate.status,
    ]),
  );
  const missing = records.filter((record) => !remote.has(record.fingerprint));
  const extra = [...remote.keys()].filter(
    (print) => !records.some((record) => record.fingerprint === print),
  );
  const statusMismatch = records.filter((record) => {
    const status = remote.get(record.fingerprint);
    return status && (status === "revoked") !== Boolean(record.revoked);
  });

  if (missing.length) {
    problems.push(`${missing.length} certificate(s) from the log are missing.`);
  }
  if (extra.length) {
    problems.push(`${extra.length} certificate(s) are not in the log.`);
  }
  if (statusMismatch.length) {
    problems.push(
      `${statusMismatch.length} certificate(s) have a different revocation status.`,
    );
  }
  return problems;
}

function finding(
  rule: string,
  kind: string,
  title: string,
  severity: Severity,
  event: PublicEvent,
  detail: string,
  evidence: PublicEvent[],
): WatchFinding {
  return {
    id: `${rule}:${kind}:${event.event_id}`,
    rule,
    title,
    severity,
    wrpId: event.wrp_id,
    observedAt: event.effective_at || event.published_at,
    detail,
    evidence,
  };
}

function label(record: CertificateRecord): string {
  return record.type === "registration_certificate"
    ? "registration certificate"
    : "access certificate";
}

function time(event: PublicEvent): number {
  return Date.parse(event.published_at);
}

function groupBy<T>(items: T[], key: (item: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const list = groups.get(key(item)) ?? [];
    list.push(item);
    groups.set(key(item), list);
  }
  return groups;
}

function severityRank(severity: Severity): number {
  return severity === "high" ? 3 : severity === "warning" ? 2 : 1;
}
